import { useCallback, useEffect, useRef, useState } from 'react';
import { getProductPriceLoader, priceInquiryIdentity } from '../utils/productPriceInquiry';

export default function useProductPriceInquiry({ client, userId, customerId,
  organizationId, productId, enabled, offline, onPrice, priority = false, retry = false, timeoutMs = 12000 }) {
  const loader = getProductPriceLoader(client);
  const key = priceInquiryIdentity({ userId, customerId, organizationId, productId });
  const current = useRef(key), mounted = useRef(true), callback = useRef(onPrice);
  const sequence = useRef(0), request = useRef(null);
  const failures = useRef({ key: null, count: 0 });
  current.current = key;
  callback.current = onPrice;
  const [state, setState] = useState(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    mounted.current = true;
    const release = loader.retain();
    const refresh = () => {
      sequence.current += 1;
      request.current = null;
      failures.current = { key: null, count: 0 };
      setState(null);
      setRevision((value) => value + 1);
    };
    window.addEventListener('msales-products-changed', refresh);
    return () => {
      mounted.current = false;
      sequence.current += 1;
      request.current = null;
      release();
      window.removeEventListener('msales-products-changed', refresh);
    };
  }, [loader]);
  const load = useCallback((options = {}) => {
    if (!enabled || offline || !customerId || !productId) return Promise.resolve(null);
    const input = { userId, customerId, organizationId, productId };
    if (request.current?.key === key) {
      if (priority || options.priority) loader.prioritize(input);
      return request.current.promise;
    }
    const version = ++sequence.current;
    const isCurrent = () => mounted.current && current.current === key && sequence.current === version;
    setState({ key, loading: true });
    const promise = loader.load(input, { priority, ...options, timeoutMs })
      .then((quote) => {
        if (!isCurrent()) return null;
        failures.current = { key, count: 0 };
        setState({ key, quote });
        if (callback.current) callback.current(quote);
        return quote;
      }).catch((error) => {
        if (isCurrent()) {
          failures.current = { key, count: failures.current.key === key ? failures.current.count + 1 : 1 };
          setState({ key, error });
        }
        return null;
      }).finally(() => {
        if (request.current?.promise === promise) request.current = null;
      });
    request.current = { key, promise };
    return promise;
  }, [key, loader, enabled, offline, userId, customerId, organizationId, productId, priority, timeoutMs]);
  const active = state?.key === key ? state : null;
  useEffect(() => {
    if (!retry || !enabled || offline || !active?.error) return undefined;
    // Keep failed/empty/expired inquiries retryable on slow links without UI
    // changes. Back off per card, while the shared loader still batches/caps work.
    const delay = Math.min(30000, 2000 * 2 ** Math.min(failures.current.count - 1, 4));
    const timer = setTimeout(() => load({ force: true }), delay);
    const reconnect = () => { clearTimeout(timer); load({ force: true }); };
    window.addEventListener('online', reconnect);
    return () => { clearTimeout(timer); window.removeEventListener('online', reconnect); };
  }, [retry, enabled, offline, active, load]);
  return { price: active?.quote || null, loading: Boolean(active?.loading), error: active?.error, load, revision };
}
